import { isEqual } from 'lodash';
import { ComparisonOperators } from './comparison-operators';
export var ElementType;
(function (ElementType) {
    ElementType["AND"] = "AND";
    ElementType["OR"] = "OR";
    ElementType["FILTER"] = "filter";
})(ElementType || (ElementType = {}));
export class FovConverter {
    constructor() {
        this.comparators = {};
        this.comparators = {
            [ComparisonOperators.MATCH_EQUALS]: (exactlyValue, comparedValue) => {
                return exactlyValue === comparedValue;
            },
            [ComparisonOperators.MATCH_CONTAINS]: (exactlyValue, comparedValue) => {
                return exactlyValue.includes(comparedValue);
            },
            [ComparisonOperators.MATCH_STARTS_WITH]: (exactlyValue, comparedValue) => {
                return exactlyValue.startsWith(comparedValue);
            },
            [ComparisonOperators.MATCH_ENDS_WITH]: (exactlyValue, comparedValue) => {
                return exactlyValue.endsWith(comparedValue);
            },
            [ComparisonOperators.IS_EMPTY]: () => {
                return false;
            },
            [ComparisonOperators.MATCH_REGEX]: (exactlyValue, comparedValue) => {
                return new RegExp(comparedValue.replace(/\\/g, '\\\\')).test(exactlyValue);
            },
        };
    }
    flatten(fov) {
        const finalFov = { nodeType: ElementType.AND, children: [] };
        fov.children.forEach((child) => {
            // We are on the first level, under the AND
            // We will process each branch independently and then aggregate the result
            if (!this.isNode(child)) {
                finalFov.children.push(this.wrapLeafInNodeOfType(child, ElementType.OR));
                return;
            }
            if (this.isLastBranch(child)) {
                finalFov.children.push(child);
                return;
            }
            // This is the second level, after this one, we assess that there is only one more level left
            if (this.hasUniqueChild(child)) {
                // If the branch contains 1 child and it's a node.
                // TODO : check if we are sure of this
                const [uniqueChild] = child.children;
                switch (uniqueChild.nodeType) {
                    case ElementType.AND:
                        finalFov.children.push(...uniqueChild.children.map((c) => this.wrapLeafInNodeOfType(c, ElementType.OR)));
                        return;
                    case ElementType.OR:
                        const newBranch = {
                            nodeType: ElementType.OR,
                            children: [],
                        };
                        newBranch.children.push(...uniqueChild.children);
                        finalFov.children.push(newBranch);
                        return;
                    default:
                        return;
                }
            }
            // In the case it's a complex branch and it has no unique child, we need to distribute over the branch nodeType
            const children = child.children.map((sChild) => {
                if (!this.isNode(sChild)) {
                    return this.wrapLeafInNodeOfType(sChild, ElementType.AND);
                }
                // TODO : check if we are sure of this
                return sChild;
            });
            if (this.hasOrNode(child)) {
                children.forEach((sChild) => {
                    switch (sChild.nodeType) {
                        case ElementType.OR:
                            finalFov.children.push({
                                nodeType: ElementType.OR,
                                children: sChild.children.reduce((p, n) => p.concat(n), []),
                            });
                            return;
                        case ElementType.AND:
                            finalFov.children.push(...sChild.children.map((andLeaf) => ({
                                nodeType: ElementType.OR,
                                children: [andLeaf],
                            })));
                        default:
                            return;
                    }
                });
                return;
            }
            // Case we have only AND nodes
            if (this.allNodesHaveOneChild(child)) {
                finalFov.children.push({
                    nodeType: ElementType.OR,
                    children: children.reduce((p, n) => p.concat(n.children), []),
                });
                return;
            }
            finalFov.children.push(...this.distributeAnd(children));
        });
        return this.optimizeFilters(finalFov);
    }
    distributeAnd([firstNode, ...nodes]) {
        // A + (B . C) => (A + B) . (A + C)
        // (A . B) + (C . D) => (A + C) . (A + D) . (B + C) . (B + D)
        // (A . B) + (C . D) + (E . F) => ((A + C) . (A + D) . (B + C) . (B + D)) + (E . F)
        //  => (A + C + E) . (A + D + E) . (B + C + E) . (B + D + E) . (A + C + F) . (A + D + F) . (B + C + F) . (B + D + F)
        const children = [];
        const nodesToProcess = nodes.map((node) => {
            if (this.isNode(node) && this.hasUniqueChild(node)) {
                return {
                    nodeType: node.nodeType,
                    children: [...node.children, ...node.children],
                };
            }
            return node;
        });
        firstNode.children.forEach((child) => {
            const leftElements = [];
            const rightElements = [];
            nodesToProcess.forEach((node) => {
                leftElements.push(node.children[0]);
                rightElements.push(node.children[1]);
            });
            children.push({
                nodeType: ElementType.OR,
                children: [child, ...leftElements],
            });
            children.push({
                nodeType: ElementType.OR,
                children: [child, ...rightElements],
            });
        });
        return children;
    }
    isLastBranch(branch) {
        return branch.children.every((element) => !this.isNode(element));
    }
    isNode(element) {
        return !!element.children;
    }
    hasOrNode(branch) {
        return !!branch.children.find((element) => element.nodeType === ElementType.OR);
    }
    allNodesHaveOneChild(branch) {
        let allNodesHaveOneChild = true;
        branch.children.forEach((node) => {
            allNodesHaveOneChild = allNodesHaveOneChild && this.hasUniqueChild(node);
        });
        return allNodesHaveOneChild;
    }
    hasUniqueChild(node) {
        return node.children.length === 1;
    }
    wrapLeafInNodeOfType(leaf, nodeType) {
        return {
            nodeType,
            children: [leaf],
        };
    }
    optimizeFilters(node) {
        const childrenToRemove = new Set();
        if (!node.children) {
            return node;
        }
        for (let indexChild = 0; indexChild < node.children.length; indexChild++) {
            const childNode = node.children[indexChild];
            if (childrenToRemove.has(childNode)) {
                continue;
            }
            if (childNode.nodeType !== ElementType.FILTER) {
                this.optimizeFilters(childNode);
                if (childNode.children && childNode.children.length === 0) {
                    childrenToRemove.add(childNode);
                }
                continue;
            }
            for (let indexExactly = 0; indexExactly < node.children.length; indexExactly++) {
                const exactlyNode = node.children[indexExactly];
                if (exactlyNode.nodeType !== ElementType.FILTER ||
                    childrenToRemove.has(exactlyNode) ||
                    !this.isExactlyOperator(exactlyNode.operator) ||
                    indexExactly === indexChild ||
                    exactlyNode.field !== childNode.field) {
                    continue;
                }
                // exactlyNode is a filter node with operator 'is_empty' or 'equals'
                // we need compare with all children for determinate if we have useless nodes
                if (this.isUselessNode(exactlyNode, childNode)) {
                    // in OR condition we need to drop exactlyChild
                    // exemples:
                    // equals "miaou" OR contains "mia" === contains "mia"
                    // in AND condition we need to drop nodeChild
                    // exemples:
                    // equals "miaou" AND contains "mia" === equals "miaou"
                    const dropIndex = node.nodeType === ElementType.AND ? indexChild : indexExactly;
                    childrenToRemove.add(node.children[dropIndex]);
                }
                else if (node.nodeType === ElementType.AND) {
                    // DROP NEEDED. this condition will be always false.
                    // exemples:
                    // is_empty AND contains "miaou"
                    // equals "haha" AND contains "toto"
                    // equals "toto" AND regex_matches "[abc]+"
                    // ...
                    node.children = [];
                }
            }
        }
        node.children = this.cleanNodes(node.children, childrenToRemove);
        return node;
    }
    isUselessNode(exactlyNode, comparedNode) {
        const matches = comparedNode.operator.match(/^(not_)?(.+)$/);
        if (matches === null) {
            return;
        }
        const not = !!matches[1];
        const operator = matches[2];
        if (!this.comparators[operator]) {
            console.error(`Operator not supported ${operator}`);
        }
        const condition = exactlyNode.operator === ComparisonOperators.IS_EMPTY
            ? operator === ComparisonOperators.IS_EMPTY
            : this.comparators[operator](exactlyNode.value, comparedNode.value);
        return not !== condition;
    }
    cleanNodes(nodes, toRemove) {
        return this.unique(nodes.filter((node) => !toRemove.has(node)));
    }
    unique(array) {
        return array.filter((item, index, list) => list.findIndex((a) => isEqual(item, a)) === index);
    }
    isExactlyOperator(operator) {
        return operator === ComparisonOperators.IS_EMPTY || operator === ComparisonOperators.MATCH_EQUALS;
    }
    static isEmptyFov(fov) {
        if (typeof fov === 'string') {
            fov = JSON.parse(fov);
        }
        if (fov.nodeType === ElementType.FILTER) {
            return false;
        }
        else {
            return fov.children.length === 0 || !fov.children.some((children) => !this.isEmptyFov(children));
        }
    }
}
//# sourceMappingURL=fov-converter.js.map